import React from "react";
import { Navbar, Button, Spinner, Container } from "react-bootstrap";
import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";

import { useSelector } from "react-redux";

import MappingWindow from "./MappingWindow";
import Subscription from "./Subscription";
import Confirmation from "./Confirmation";

import { getUrl } from "../helpers/url";

import { useDispatch } from "react-redux";

import { setShowErrorModal, setShowSaveModal } from "../store/slice/modalSlice";
import { updateEnableEditing } from "../store/slice/mappingSlice";

import { CmsConstructor } from "../helpers/cmsConstructor";
import { useCmsHook } from "../helpers/cmsHook";
import CmsEditAndSave from "../helpers/CmsEditAndSave";

function NavBarPage() {
  const { setCmsInstance, cmsInstance, isEdit } = useCmsHook();

  const dispatch = useDispatch();
  const isDirty = useSelector((state) => state.mapping.isDirty);
  const { isBtnDisabled } = useSelector((state) => state.mapping);
  const deletePdProperties = useSelector(
    (state) => state.mapping.deletePipedriveFields
  );
  const [currentWindow, setCurrentWindow] = useState("");
  const [loading, setLoading] = useState(false);
  const effectRan = useRef(false);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const WINDOWS = Object.freeze({
    MAPPING_WINDOW: "Mapping Window",
    SUBSCRIPTION_WINDOW: "Subscription Window",
    CONFIRMATION_WINDOW: "Confirmation Window",
  });

  const windows = {
    [WINDOWS.MAPPING_WINDOW]: <MappingWindow />,
    [WINDOWS.SUBSCRIPTION_WINDOW]: <Subscription />,
    [WINDOWS.CONFIRMATION_WINDOW]: <Confirmation />,
  };

  // This function is responsible for navigating components inside the setup page by clicking on next button
  const onNextNavigation = () => {
    const companyId = searchParams.get("companyId");
    const userId = searchParams.get("userId");
    if (currentWindow === WINDOWS.SUBSCRIPTION_WINDOW) {
      navigate(
        `${window.location.pathname}?companyId=${companyId}&userId=${userId}&confirmation=true`
      );
      setCurrentWindow(WINDOWS.CONFIRMATION_WINDOW);
    }
    if (currentWindow === WINDOWS.MAPPING_WINDOW) {
      dispatch(updateEnableEditing(false));
      navigate(
        `${window.location.pathname}?companyId=${companyId}&userId=${userId}&paymentStatus=none`
      );
      setCurrentWindow(WINDOWS.SUBSCRIPTION_WINDOW);
    }
  };

  // This function is responsible for navigating components inside the setup page by clicking on back button
  const onBackNavigation = () => {
    const companyId = searchParams.get("companyId");
    const userId = searchParams.get("userId");
    if (currentWindow === WINDOWS.CONFIRMATION_WINDOW) {
      navigate(
        `${window.location.pathname}?companyId=${companyId}&userId=${userId}&paymentStatus=none`
      );
      setCurrentWindow(WINDOWS.SUBSCRIPTION_WINDOW);
    }
    if (currentWindow === WINDOWS.SUBSCRIPTION_WINDOW) {
      navigate(
        `${window.location.pathname}?companyId=${companyId}&userId=${userId}`
      );
      setCurrentWindow(WINDOWS.MAPPING_WINDOW);
    }
  };

  // This function responsible for navigating the user to pipedrive website when clicked on finish button
  const goBackToPipedrive = () => {
    const pdCompanyDomain = JSON.parse(
      sessionStorage.getItem("pdCompanyDomain")
    );
    window.open(`https://${pdCompanyDomain}.pipedrive.com/`, "_self");
  };

  // This function is responsible for installing the user for the first time
  const install = async () => {
    try {
      const code = searchParams.get("code");
      const companyId = searchParams.get("companyId");
      const userId = searchParams.get("userId");
      const paymentStatus = searchParams.get("paymentStatus");
      const confirmation = searchParams.get("confirmation");
      // Rendering the components inside this setup page based on the params present in the url
      if (code) {
        setLoading(true);
        let { data: res } = await axios.post(getUrl("REACT_APP_INSTALL_URL"), {
          code,
        });
        console.log(res);
        sessionStorage.setItem(
          "pdCompanyDomain",
          JSON.stringify(res.companyResp.companyData["company_domain"])
        );
        setCurrentWindow(WINDOWS.MAPPING_WINDOW);
        setLoading(false);
        navigate(
          `../setup?companyId=${res.companyResp.companyData["company_id"]}&userId=${res.contactResp.id}`
        );
      } else if (companyId && userId && confirmation) {
        setCurrentWindow(WINDOWS.CONFIRMATION_WINDOW);
      } else if (companyId && userId && paymentStatus) {
        setCurrentWindow(WINDOWS.SUBSCRIPTION_WINDOW);
      } else if (companyId && userId) {
        setCurrentWindow(WINDOWS.MAPPING_WINDOW);
      }
    } catch (error) {
      console.log(error);
      let message;
      if (error.response && error.response.data.message) {
        message = error.response.data.message;
      } else message = error.message;
      dispatch(setShowErrorModal({ message: message }));
    }
  };

  useEffect(() => {
    const cmsInstance = async () => {
      const cmsObj = await CmsConstructor(dispatch);
      setCmsInstance(cmsObj);
    };
    cmsInstance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Prevented useEffect firing twice in development by using the below condition
    if (effectRan.current === false) {
      install();
      return () => {
        effectRan.current = true;
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div>
        {loading ? (
          <div className="d-flex flex-column justify-content-center align-items-center nav-loading-container">
            <div className="d-flex flex-row justify-content-center align-items-center">
              <Spinner animation="border" variant="success" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          </div>
        ) : (
          <div className="navbar-page-bg-container">
            <Navbar fixed="top" className="px-4 py-3 navbar">
              <div className="d-flex justify-content-between w-100">
                {currentWindow !== "Mapping Window" ? (
                  <Button
                    className="navbar-back-button my-auto"
                    onClick={onBackNavigation}
                  >
                    Back
                  </Button>
                ) : (
                  <p className="disable-para"></p>
                )}
                <div className="text-center header-content">
                  {cmsInstance &&
                    cmsInstance.showRichText("cmsContent.header.text")}
                </div>
                {currentWindow !== "Confirmation Window" ? (
                  <>
                    {(isDirty && currentWindow === "Mapping Window") ||
                    (currentWindow === "Mapping Window" && isBtnDisabled) ||
                    (deletePdProperties && deletePdProperties.length > 0) ? (
                      <Button
                        className="navbar-back-button my-auto"
                        onClick={() => dispatch(setShowSaveModal())}
                      >
                        Next
                      </Button>
                    ) : (
                      <Button
                        className="navbar-back-button my-auto"
                        onClick={onNextNavigation}
                      >
                        Next
                      </Button>
                    )}
                  </>
                ) : (
                  <Button
                    className="navbar-finish-button my-auto"
                    onClick={goBackToPipedrive}
                  >
                    Finish
                  </Button>
                )}
              </div>
            </Navbar>

            <Container className="components-container">
              <div
                className="d-flex flex-row justify-content-center"
                style={{
                  marginTop:
                    isEdit && window.location.pathname === "/setup" && "150px",
                }}
              >
                <CmsEditAndSave />
              </div>
              <div>{windows[currentWindow]}</div>
            </Container>
          </div>
        )}
      </div>
    </>
  );
}

export default NavBarPage;
